import { useTheme } from '@material-ui/core';
import { themeAlpine } from 'ag-grid-enterprise';
import { useEffect, useRef } from 'react';
import { pxToRem } from '../theme';
/**
 * Custom hook that manages DataTable functionality
 *
 * @param {UseDataTableParams} params - Parameters for the hook
 * @returns Object containing handlers and refs for the DataTable component
 */
export const useDataTable = ({ fixedBottomScroll = false, fixedHeader = false, rowData, onGridReady, onModelUpdated, }) => {
    const theme = useTheme();
    const tableRef = useRef(null);
    const headerRef = useRef(null);
    const headerPositionRef = useRef(0);
    const headerWidthRef = useRef(0);
    const headerLeftRef = useRef(0);
    const tableBodyRef = useRef(null);
    const scrollBarRef = useRef(null);
    const scrollBarWidthRef = useRef(0);
    const scrollBarLeftRef = useRef(0);
    const gridApiRef = useRef(null);
    const setupScrollHandling = () => {
        if (!tableRef.current)
            return;
        const cacheElementPositions = () => {
            if (!tableRef.current)
                return;
            //find header element if not already found
            if (!headerRef.current) {
                headerRef.current = tableRef.current.querySelector('.ag-header');
            }
            if (headerRef.current) {
                const rect = headerRef.current.getBoundingClientRect();
                headerPositionRef.current = rect.top + window.scrollY;
                headerWidthRef.current = rect.width;
                headerLeftRef.current = rect.left;
            }
            //find scroll bar element if fixedBottomScroll is enabled
            if (fixedBottomScroll && !scrollBarRef.current) {
                scrollBarRef.current = tableRef.current.querySelector('.ag-body-horizontal-scroll');
            }
            if (scrollBarRef.current) {
                const rect = scrollBarRef.current.getBoundingClientRect();
                scrollBarWidthRef.current = rect.width;
                scrollBarLeftRef.current = rect.left;
            }
        };
        const handleScroll = () => {
            //cache positions of header and scrollbar
            if ((!headerRef.current && fixedHeader) || (!scrollBarRef.current && fixedBottomScroll)) {
                cacheElementPositions();
                return;
            }
            tableBodyRef.current = tableRef.current?.querySelector('.ag-body-viewport') ?? null;
            //handle fixedHeader if enabled
            if (fixedHeader && headerRef.current) {
                //determine if we should fix the header
                const shouldFixHeader = window.scrollY + headerHeight > headerPositionRef.current;
                if (shouldFixHeader) {
                    headerRef.current.classList.add('fixed-header');
                    //set inline styles to maintain correct position and width since this is dynamically calculated
                    headerRef.current.style.width = `${headerWidthRef.current}px`;
                    headerRef.current.style.left = `${headerLeftRef.current}px`;
                    if (tableBodyRef.current) {
                        tableBodyRef.current.classList.add('fixed-header');
                    }
                }
                else {
                    headerRef.current.classList.remove('fixed-header');
                    headerRef.current.style.width = '';
                    headerRef.current.style.left = '';
                    if (tableBodyRef.current) {
                        tableBodyRef.current.classList.remove('fixed-header');
                    }
                }
            }
            //fixedBottomScroll bar if enabled, always remains present
            if (fixedBottomScroll && scrollBarRef.current) {
                scrollBarRef.current.classList.add('fixed-bottom');
                //set inline styles to maintain correct position and width since this is dynamically calculated
                scrollBarRef.current.style.width = `${scrollBarWidthRef.current}px`;
                scrollBarRef.current.style.left = `${scrollBarLeftRef.current}px`;
            }
        };
        //initial checks
        cacheElementPositions();
        handleScroll();
        //handle resize to recalculate dimensions
        const handleResize = () => {
            cacheElementPositions();
            handleScroll();
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            //reset states when component unmounts
            if (headerRef.current) {
                headerRef.current.classList.remove('fixed-header');
                headerRef.current.style.width = '';
                headerRef.current.style.left = '';
            }
            if (scrollBarRef.current) {
                scrollBarRef.current.classList.remove('fixed-bottom');
                scrollBarRef.current.style.width = '';
                scrollBarRef.current.style.left = '';
            }
        };
    };
    useEffect(() => {
        //return early if neither fixedHeader/fixedBottomScroll is enabled
        if (!fixedHeader && !fixedBottomScroll)
            return;
        return setupScrollHandling();
    }, [fixedHeader, fixedBottomScroll]);
    //watch for rowData changes via params
    useEffect(() => {
        //if grid is ready and we have fixed header/scrollbar enabled, reinitialize after data changes
        if (gridApiRef.current && (fixedHeader || fixedBottomScroll)) {
            setTimeout(() => {
                const cleanup = setupScrollHandling();
                return () => {
                    if (cleanup)
                        cleanup();
                };
            }, 100);
        }
    }, [rowData, fixedHeader, fixedBottomScroll]);
    const handleGridReady = (params) => {
        gridApiRef.current = params.api;
        setTimeout(() => {
            if (fixedHeader || fixedBottomScroll) {
                const cleanup = setupScrollHandling();
                return () => {
                    if (cleanup)
                        cleanup();
                };
            }
        }, 100);
        //call the original onGridReady from params
        if (onGridReady) {
            onGridReady(params);
        }
    };
    //handle model updated event (fires when data changes, filtering occurs, etc.)
    const handleModelUpdated = (params) => {
        if (fixedHeader || fixedBottomScroll) {
            setTimeout(() => {
                setupScrollHandling();
            }, 100);
        }
        //call the original onModelUpdated from params
        if (onModelUpdated) {
            onModelUpdated(params);
        }
    };
    const getGridTheme = () => {
        return themeAlpine.withParams({
            accentColor: theme.palette.primary.main,
            borderColor: theme.palette.divider,
            selectCellBackgroundColor: theme.palette.primary.light,
            chromeBackgroundColor: '#f2f5f6',
            columnDropCellBackgroundColor: '#d2eaF1',
            fontFamily: 'Inter',
            fontSize: pxToRem(12),
            headerBackgroundColor: '#f2f5f6',
            headerCellHoverBackgroundColor: 'transparent',
            headerCellMovingBackgroundColor: 'rgba(91, 106, 117, 0.12)',
            headerFontSize: pxToRem(12),
            headerFontWeight: 600,
            headerHeight: 40,
            iconSize: 18,
            inputTextColor: theme.palette.text.primary,
            inputFocusShadow: 'none',
            inputFocusBorder: { color: theme.palette.primary.main, width: 2 },
            panelBackgroundColor: '#f2f5f6',
            rangeSelectionBorderColor: 'transparent',
            rowHeight: 40,
            rowHoverColor: theme.palette.brand.sky25,
            selectedRowBackgroundColor: theme.palette.primary.light,
            spacing: theme.spacing(1),
            textColor: theme.palette.text.primary,
        });
    };
    const getCustomStyles = () => {
        return `
      .ag-filter-apply-panel-button {
        background-color: transparent;
        border-color: ${theme.palette.statesPrimary.outlinedBorder};
        color: ${theme.palette.primary.main};
        font-size: ${pxToRem(13)};
        font-weight: 600;
        line-height: ${pxToRem(22)};
        padding: ${pxToRem(4, 10)};
      }
    `;
    };
    return {
        tableRef,
        handleGridReady,
        handleModelUpdated,
        getGridTheme,
        getCustomStyles,
    };
};
// Header height constant
export const headerHeight = 117; //header height across ANP and ANS is 117px
